import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import axios from "axios";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import utilities from "Admin/js/Helpers/utilities";
import { useConversation } from "Admin/js/Stores/Conversation";

/**
 * Normalized workspace tree store.
 *
 * All entities (workspace categories, workspaces, board categories, boards,
 * item categories, items) are stored in flat reactive lookup objects keyed by
 * their ID. Relationships and display order are maintained via `*OrderedIds`
 * arrays on parent entities rather than deep nesting.
 *
 * Layer hierarchy:
 *   workspaceCategory → workspace → boardCategory → board → itemCategory → item
 *
 * Design notes:
 * - Never mutate state directly from outside this store — use the exposed
 *   local (add / sync / remove) or API functions.
 * - URL computeds depend on the active entity slugs and return `null` when
 *   prerequisites are not set, so always guard before consuming them.
 * - Data placement functions (`place*Data`) are called after load API responses
 *   and are the single source of truth for hydrating the lookup tables.
 */
export const useWorkspaceTree = defineStore("workspaceTree", () => {
    const { manageAsyncOperation } = useAsyncOperation();

    function notifyWorkspaceTreeApiError(err) {
        if (err?.response?.status === 403) {
            utilities().showErrorSnackbar("Permission Denied");
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // FLAT LOOKUP TABLES
    // Each entity type lives in its own plain reactive object keyed by entity
    // ID. Plain objects (not Map) are used intentionally so Vue tracks
    // per-key reactivity — only computed properties that read a specific key
    // re-evaluate when that key changes.
    // ─────────────────────────────────────────────────────────────────────────

    const workspaceCategoryOrderedIds = ref([]);
    const workspaceCategoriesById = ref({});
    const workspacesById = ref({});
    const boardCategoriesById = ref({});
    const boardsById = ref({});
    const itemCategoriesById = ref({});
    const itemsById = ref({});

    // ─────────────────────────────────────────────────────────────────────────
    // LOADING STATE
    // One boolean ref per logical operation group. Naming convention:
    //   isLoading*   → read/fetch operations
    //   isDefining*  → create and update (both use the same loader since they
    //                  share the same form/UI state in the product)
    //   isDeleting*  → delete operations
    //   isReordering* → drag-and-drop reorder operations
    // Pass the matching ref's setter as the `setLoading` callback to
    // `manageAsyncOperation` so it is toggled automatically on start/finish.
    // ─────────────────────────────────────────────────────────────────────────

    const isLoadingSpaces = ref(false);
    const isLoadingBoards = ref(false);
    const isLoadingItems = ref(false);

    const isDefiningWorkspaceCategory = ref(false);
    const isDefiningWorkspace = ref(false);
    const isDefiningBoardCategory = ref(false);
    const isDefiningBoard = ref(false);
    const isDefiningItemCategory = ref(false);
    const isDefiningItem = ref(false);

    const isDeletingWorkspaceCategory = ref(false);
    const isDeletingWorkspace = ref(false);
    const isDeletingBoardCategory = ref(false);
    const isDeletingBoard = ref(false);
    const isDeletingItemCategory = ref(false);
    const isDeletingItem = ref(false);

    const isReorderingWorkspace = ref(false);
    const isReorderingBoard = ref(false);
    const isReorderingItem = ref(false);

    const fetching_item = ref(false);

    /**
     * Sets the `fetching_item` loading flag.
     *
     * @param {any} value - Truthy value sets the flag to `true`; falsy sets it to `false`.
     */
    function setIsFetchingItem(value) {
        fetching_item.value = Boolean(value);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PENDING NEW WORKSPACE
    // Tracks a workspace staged for creation via the drawer. The component
    // renders a temporary inline row so the user can type a name. Cleared on
    // save or cancel.
    // ─────────────────────────────────────────────────────────────────────────

    const pendingNewWorkspaceCategoryId = ref(null);
    const pendingNewWorkspaceType = ref(null);
    const newItemCategoryId = ref(null);

    /**
     * Stages a new workspace for creation by recording its parent category and type.
     * The UI renders a temporary inline row based on these values.
     *
     * @param {number|string} categoryId - ID of the parent workspace category.
     * @param {string}        type       - Type of workspace to create (e.g. `"private"`, `"public"`).
     */
    function setPendingNewWorkspace(categoryId, type) {
        pendingNewWorkspaceCategoryId.value = categoryId;
        pendingNewWorkspaceType.value = type;
    }

    /**
     * Clears the pending new workspace state, hiding the inline creation row.
     * Call this on save or cancel.
     */
    function clearPendingNewWorkspace() {
        pendingNewWorkspaceCategoryId.value = null;
        pendingNewWorkspaceType.value = null;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIVE STATE
    // Tracks which entity is currently selected at each layer. Stored as IDs
    // only — never as entity objects — so reactivity stays fine-grained.
    // Setting a parent-layer active ID cascades resets down to all child layers.
    // ─────────────────────────────────────────────────────────────────────────

    const activeWorkspaceCategoryId = ref(null);
    const activeWorkspaceId = ref(null);
    const activeBoardCategoryId = ref(null);
    const activeBoardId = ref(null);
    const activeItemCategoryId = ref(null);
    const activeItemId = ref(null);

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIVE SETTERS
    // Always use these setters instead of mutating the refs directly so the
    // cascading-reset contract is guaranteed. For example, switching the active
    // workspace must clear any board / item selection that belonged to the
    // previous workspace.
    // ─────────────────────────────────────────────────────────────────────────

    /** @param {number|null} id @param {boolean} [autoredirect=false] */
    function setActiveWorkspaceCategoryId(id, autoredirect = false) {
        activeWorkspaceCategoryId.value = id;
        if (autoredirect && id != null) {
            syncWorkspaceCategoryById(id, { redirect: true });
        }
    }

    /**
     * @param {number|null} id
     * @param {boolean} [autoredirect=false]
     */
    function setActiveWorkspaceId(id, autoredirect = false) {
        activeWorkspaceId.value = id;
        activeBoardCategoryId.value = null;
        activeBoardId.value = null;
        activeItemCategoryId.value = null;
        activeItemId.value = null;
        if (autoredirect && id != null) {
            syncWorkspaceById(id, { redirect: true });
        }
    }

    /** @param {number|null} id @param {boolean} [autoredirect=false] */
    function setActiveBoardCategoryId(id, autoredirect = false) {
        activeBoardCategoryId.value = id;
        if (autoredirect && id != null) {
            syncBoardCategoryById(id, { redirect: true });
        }
    }

    /**
     * @param {number|null} id
     * @param {boolean} [autoredirect=false]
     */
    function setActiveBoardId(id, autoredirect = false) {
        activeBoardId.value = id;
        activeItemCategoryId.value = null;
        activeItemId.value = null;
        if (autoredirect && id != null) {
            syncBoardById(id, { redirect: true });
        }
    }

    /** @param {number|null} id @param {boolean} [autoredirect=false] */
    function setActiveItemCategoryId(id, autoredirect = false) {
        activeItemCategoryId.value = id;
        if (autoredirect && id != null) {
            syncItemCategoryById(id, { redirect: true });
        }
    }

    /** @param {number|null} id @param {boolean} [autoredirect=false] */
    function setActiveItemId(id, autoredirect = false) {
        activeItemId.value = id;
        if (autoredirect && id != null) {
            syncItemById(id, { redirect: true });
        }
    }

    /** Clears all active IDs across every layer. */
    function resetActiveState() {
        activeWorkspaceCategoryId.value = null;
        activeWorkspaceId.value = null;
        activeBoardCategoryId.value = null;
        activeBoardId.value = null;
        activeItemCategoryId.value = null;
        activeItemId.value = null;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIVE COMPUTED
    // O(1) derivations of the currently active entity at each layer. All
    // return `null` when the active ID is unset or the entity is missing from
    // the lookup table — consumers must guard against null before use.
    // ─────────────────────────────────────────────────────────────────────────

    const activeWorkspaceCategory = computed(() => {
        return getWorkspaceCategoryById(activeWorkspaceCategoryId.value);
    });

    const activeWorkspace = computed(() => {
        return getWorkspaceById(activeWorkspaceId.value);
    });

    const activeBoardCategory = computed(() => {
        return getBoardCategoryById(activeBoardCategoryId.value);
    });

    const activeBoard = computed(() => {
        return getBoardById(activeBoardId.value);
    });

    const activeItemCategory = computed(() => {
        return getItemCategoryById(activeItemCategoryId.value);
    });

    const activeItem = computed(() => {
        return getItemById(activeItemId.value);
    });

    // ─────────────────────────────────────────────────────────────────────────
    // URL BUILDERS + COMPUTEDS
    // Path templates match the REST routes used by API helpers. Parameterized
    // builders are exposed for callers (e.g. invite URLs) that need a workspace
    // / board other than the active selection; computeds delegate to them.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {Object|null} workspace
     * @returns {string|null}
     */
    function getBoardCategoriesUrlForWorkspace(workspace) {
        if (!workspace?.slug) {
            return null;
        }
        return `/workspaces/${workspace.slug}/board-categories`;
    }

    /**
     * @param {Object|null} workspace
     * @returns {string|null}
     */
    function getBoardsUrlForWorkspace(workspace) {
        if (!workspace?.slug) {
            return null;
        }
        return `/workspaces/${workspace.slug}/boards`;
    }

    /**
     * @param {Object|null} workspace
     * @param {Object|null} board
     * @returns {string|null}
     */
    function getItemCategoriesUrlForWorkspaceAndBoard(workspace, board) {
        const boards = getBoardsUrlForWorkspace(workspace);
        if (!boards || !board?.slug) {
            return null;
        }
        return `${boards}/${board.slug}/item-categories`;
    }

    /**
     * @param {Object|null} workspace
     * @param {Object|null} board
     * @returns {string|null}
     */
    function getItemsUrlForWorkspaceAndBoard(workspace, board) {
        const boards = getBoardsUrlForWorkspace(workspace);
        if (!boards || !board?.slug) {
            return null;
        }
        return `${boards}/${board.slug}/items`;
    }

    const spaceCategoriesUrl = computed(() => {
        return `/workspace-categories`;
    });

    const spacesUrl = computed(() => {
        return `/workspaces`;
    });

    const boardCategoriesUrl = computed(() =>
        getBoardCategoriesUrlForWorkspace(activeWorkspace.value),
    );

    const boardsUrl = computed(() =>
        getBoardsUrlForWorkspace(activeWorkspace.value),
    );

    const itemCategoriesUrl = computed(() =>
        getItemCategoriesUrlForWorkspaceAndBoard(
            activeWorkspace.value,
            activeBoard.value,
        ),
    );

    const itemsUrl = computed(() =>
        getItemsUrlForWorkspaceAndBoard(
            activeWorkspace.value,
            activeBoard.value,
        ),
    );

    // ─────────────────────────────────────────────────────────────────────────
    // DATA PLACEMENT (internal)
    // Called exclusively after API load responses. Each function normalizes a
    // nested API payload into the flat lookup tables, attaches parent/ancestor
    // IDs to every child entity, and writes ordered ID arrays back onto the
    // parent so the insertion order from the server is preserved.
    // Never call these from outside this store.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Hydrates the flat lookup tables from the workspace-categories API response.
     * Strips nested `workspaces` from each category and populates `workspacesById`
     * with parent IDs attached, preserving insertion order in `workspaceOrderedIds`.
     *
     * @param {Array} categories
     */
    function placeWorkspaceCategoriesData(categories) {
        const categoryOrderedIds = [];
        categories.forEach((category) => {
            const { workspaces, ...workspaceCategory } = category;
            const workspaceOrderedIds = [];
            workspaces?.forEach((workspace) => {
                Object.assign(workspace, {
                    workspaceCategoryId: category.id,
                    boardCategoryOrderedIds: null,
                });
                workspacesById.value[workspace.id] = workspace;
                workspaceOrderedIds.push(workspace.id);
            });
            workspaceCategory.workspaceOrderedIds = workspaceOrderedIds;
            workspaceCategoriesById.value[category.id] = workspaceCategory;
            categoryOrderedIds.push(category.id);
        });
        workspaceCategoryOrderedIds.value = categoryOrderedIds;
    }

    /**
     * Hydrates board categories and boards from the boards API response.
     * Attaches ancestry IDs to each entity and writes `boardCategoryOrderedIds`
     * back onto the parent workspace.
     *
     * @param {Array}  boardCategories
     * @param {Object} workspace - Must include `id`, `slug`, and `workspaceCategoryId`.
     */
    function placeBoardCategoriesData(boardCategories, workspace) {
        const { workspaceCategoryId } = workspace;
        const boardCategoryOrderedIds = [];

        boardCategories.forEach((category) => {
            Object.assign(category, {
                workspaceId: workspace.id,
                workspaceCategoryId,
            });
            const { boards, ...boardCategory } = category;

            const boardOrderedIds = [];
            boards?.forEach((board) => {
                Object.assign(board, {
                    boardCategoryId: boardCategory.id,
                    workspaceId: workspace.id,
                    workspaceCategoryId,
                    itemCategoryOrderedIds: null,
                });
                boardsById.value[board.id] = board;
                boardOrderedIds.push(board.id);
            });

            boardCategory.boardOrderedIds = boardOrderedIds;
            boardCategoriesById.value[boardCategory.id] = boardCategory;
            boardCategoryOrderedIds.push(boardCategory.id);
        });

        const ws = getWorkspaceById(workspace.id);
        if (ws) {
            ws.boardCategoryOrderedIds = boardCategoryOrderedIds;
        }
    }

    /**
     * Hydrates item categories and items from the items API response.
     * Attaches full ancestry IDs to each entity and writes `itemCategoryOrderedIds`
     * back onto the parent board.
     *
     * @param {Array}  itemCategories
     * @param {Object} board - Must include `id`, `slug`, `boardCategoryId`, `workspaceId`, and `workspaceCategoryId`.
     */
    function placeItemCategoriesData(itemCategories, board) {
        const { boardCategoryId, workspaceId, workspaceCategoryId } = board;
        const itemCategoryOrderedIds = [];

        itemCategories.forEach((category) => {
            Object.assign(category, {
                boardId: board.id,
                boardCategoryId,
                workspaceId,
                workspaceCategoryId,
            });
            const { items, ...itemCategory } = category;

            const itemOrderedIds = [];
            items?.forEach((item) => {
                Object.assign(item, {
                    itemCategoryId: itemCategory.id,
                    boardId: board.id,
                    boardCategoryId,
                    workspaceId,
                    workspaceCategoryId,
                });
                itemsById.value[item.id] = item;
                itemOrderedIds.push(item.id);
            });

            itemCategory.itemOrderedIds = itemOrderedIds;
            itemCategoriesById.value[itemCategory.id] = itemCategory;
            itemCategoryOrderedIds.push(itemCategory.id);
        });

        const boardData = getBoardById(board.id);
        if (boardData) {
            boardData.itemCategoryOrderedIds = itemCategoryOrderedIds;
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // GETTERS
    // O(1) lookups into the flat tables. Always return `null` on a miss so
    // callers never have to deal with `undefined`. Use these everywhere instead
    // of accessing the lookup refs directly — they provide a stable interface
    // if the underlying storage ever changes.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getWorkspaceCategoryById(id) {
        return workspaceCategoriesById.value[id] ?? null;
    }

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getWorkspaceById(id) {
        return workspacesById.value[id] ?? null;
    }

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getBoardCategoryById(id) {
        return boardCategoriesById.value[id] ?? null;
    }

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getBoardById(id) {
        return boardsById.value[id] ?? null;
    }

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getItemCategoryById(id) {
        return itemCategoriesById.value[id] ?? null;
    }

    /**
     * Resolves the flat-store row for an item category when the UI passes an id
     * that may be typed as string or number (object keys differ in plain objects).
     *
     * @param {{ id?: number|string }} itemCategory
     * @returns {Object|null}
     */
    function getItemCategoryEntityForApi(itemCategory) {
        if (itemCategory?.id == null) {
            return null;
        }
        return (
            getItemCategoryById(itemCategory.id) ??
            getItemCategoryById(Number(itemCategory.id)) ??
            null
        );
    }

    /**
     * @param {number} id
     * @returns {Object|null}
     */
    function getItemById(id) {
        return itemsById.value[id] ?? null;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // COMPUTED TREES
    // Reconstruct a nested view of each layer for UI consumption. These are the
    // only place where the flat tables are joined back into a hierarchy — UI
    // components should bind to these instead of building their own joins.
    // Each computed re-evaluates only when the specific lookup keys it reads
    // actually change, keeping render cycles lean.
    // ─────────────────────────────────────────────────────────────────────────

    const spaceTree = computed(() =>
        getSpaceTree(workspaceCategoryOrderedIds.value),
    );
    const boardTree = computed(() =>
        getBoardTreeForWorkspace(activeWorkspace.value),
    );
    const itemTree = computed(() => getItemTreeForBoard(activeBoard.value));

    /**
     * Returns the full space-category tree.
     * Same shape as `spaceTree` computed but callable as a plain function,
     * so components can call it without a computed wrapper.
     *
     * @param {Array} [orderedIds=workspaceCategoryOrderedIds.value] - Array of workspace category IDs.
     * @returns {Array<{ workspaces: Array<Object|null> } & Object>} Nested category objects with their workspaces.
     */
    function getSpaceTree(orderedIds = workspaceCategoryOrderedIds.value) {
        return orderedIds
            .map((catId) => {
                const category = getWorkspaceCategoryById(catId);
                if (!category) return null;
                const workspaces = category.workspaceOrderedIds.map((id) =>
                    getWorkspaceById(id),
                );
                return { ...category, workspaces };
            })
            .filter((c) => c !== null);
    }

    /**
     * Returns the board-category tree for any given workspace.
     * Same shape as `boardTree` but takes the workspace as an argument
     * instead of reading the active workspace.
     *
     * @param {Object|null} workspace - Must include `boardCategoryOrderedIds`.
     * @returns {Array<{ boards: Array<Object|null> } & Object>} Nested board-category objects with their boards.
     */
    function getBoardTreeForWorkspace(workspace) {
        if (!workspace?.boardCategoryOrderedIds) return [];
        return workspace.boardCategoryOrderedIds
            .map((catId) => {
                const category = getBoardCategoryById(catId);
                if (!category) return null;
                const boards = category.boardOrderedIds.map((id) =>
                    getBoardById(id),
                );
                return { ...category, boards };
            })
            .filter((c) => c !== null);
    }

    /**
     * Returns the item-category tree for any given board.
     * Same shape as `itemTree` but takes the board as an argument
     * instead of reading the active board.
     *
     * @param {Object|null} board - Must include `itemCategoryOrderedIds`.
     * @returns {Array<{ items: Array<Object|null> } & Object>} Nested item-category objects with their items.
     */
    function getItemTreeForBoard(board) {
        if (!board?.itemCategoryOrderedIds) return [];
        return board.itemCategoryOrderedIds
            .map((catId) => {
                const category = getItemCategoryById(catId);
                if (!category) return null;
                const items = category.itemOrderedIds.map((id) =>
                    getItemById(id),
                );
                return { ...category, items };
            })
            .filter((c) => c !== null);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // LOCAL — ADD
    // Insert a new entity into the flat table and register its ID in the
    // parent's ordered ID array so it appears correctly in the tree computeds.
    // Always include all required ancestry IDs on the entity before calling
    // these (workspaceCategoryId, workspaceId, boardCategoryId, etc.).
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {Object} workspaceCategory
     */
    function addWorkspaceCategory(workspaceCategory) {
        const { workspaces, organization, ...category } = workspaceCategory;

        const workspaceOrderedIds = [];
        workspaces?.forEach((workspace) => {
            workspacesById.value[workspace.id] = {
                ...workspace,
                workspaceCategoryId: category.id,
                boardCategoryOrderedIds: null,
            };
            workspaceOrderedIds.push(workspace.id);
        });

        workspaceCategoriesById.value[category.id] = {
            ...category,
            workspaceOrderedIds,
        };

        const ids = [...workspaceCategoryOrderedIds.value];
        ids.splice(category.order - 1, 0, category.id);
        workspaceCategoryOrderedIds.value = ids;
    }

    /**
     * @param {Object} workspace - Must include `workspaceCategoryId`.
     */
    function addWorkspace(workspace) {
        const { board_categories, organization, ...space } = workspace;

        let boardCategoryOrderedIds = null;
        if (board_categories) {
            boardCategoryOrderedIds = [];
            board_categories.forEach((category) => {
                const { boards, ...boardCategory } = category;

                const boardOrderedIds = [];
                boards?.forEach((board) => {
                    addBoard({
                        ...board,
                        boardCategoryId: boardCategory.id,
                        workspaceId: space.id,
                        workspaceCategoryId: space.workspaceCategoryId,
                    });
                    boardOrderedIds.push(board.id);
                });

                boardCategoriesById.value[boardCategory.id] = {
                    ...boardCategory,
                    workspaceId: space.id,
                    workspaceCategoryId: space.workspaceCategoryId,
                    boardOrderedIds,
                };
                boardCategoryOrderedIds.push(boardCategory.id);
            });
        }

        workspacesById.value[space.id] = {
            ...space,
            boardCategoryOrderedIds,
        };

        const parent = getWorkspaceCategoryById(
            space.workspaceCategoryId ?? space.workspace_category_id,
        );
        if (parent) {
            const ids = [...(parent.workspaceOrderedIds || [])];
            ids.unshift(space.id);
            parent.workspaceOrderedIds = ids;
        }
    }

    /**
     * @param {Object} boardCategory - Must include `workspaceId`.
     */
    function addBoardCategory(boardCategory) {
        const { boards, organization, ...category } = boardCategory;

        const boardOrderedIds = [];
        boards?.forEach((board) => {
            boardsById.value[board.id] = {
                ...board,
                boardCategoryId: category.id,
                workspaceId: category.workspaceId,
                workspaceCategoryId: category.workspaceCategoryId,
                itemCategoryOrderedIds: null,
            };
            boardOrderedIds.push(board.id);
        });

        boardCategoriesById.value[category.id] = {
            ...category,
            boardOrderedIds,
        };

        const parent = getWorkspaceById(
            category.workspaceId ?? category.workspace_id,
        );
        if (parent) {
            const ids = [...(parent.boardCategoryOrderedIds || [])];
            ids.splice(category.order - 1, 0, category.id);
            parent.boardCategoryOrderedIds = ids;
        }
    }

    /**
     * @param {Object} board - Must include `boardCategoryId`.
     */
    function addBoard(board) {
        const { item_categories, board_category, organization, ...boardData } =
            board;

        let itemCategoryOrderedIds = null;
        if (item_categories) {
            itemCategoryOrderedIds = [];
            item_categories.forEach((category) => {
                const { items, ...itemCategory } = category;

                const itemOrderedIds = [];
                items?.forEach((item) => {
                    itemsById.value[item.id] = {
                        ...item,
                        itemCategoryId: itemCategory.id,
                        boardId: boardData.id,
                        boardCategoryId: boardData.boardCategoryId,
                        workspaceId: boardData.workspaceId,
                        workspaceCategoryId: boardData.workspaceCategoryId,
                    };
                    itemOrderedIds.push(item.id);
                });

                itemCategoriesById.value[itemCategory.id] = {
                    ...itemCategory,
                    boardId: boardData.id,
                    boardCategoryId: boardData.boardCategoryId,
                    workspaceId: boardData.workspaceId,
                    workspaceCategoryId: boardData.workspaceCategoryId,
                    itemOrderedIds,
                };
                itemCategoryOrderedIds.push(itemCategory.id);
            });
        }

        boardsById.value[boardData.id] = {
            ...boardData,
            itemCategoryOrderedIds,
        };

        const parent = getBoardCategoryById(
            boardData.boardCategoryId ?? boardData.board_category_id,
        );
        if (parent) {
            parent.boardOrderedIds.push(boardData.id);
        }
    }

    /**
     * @param {Object} itemCategory - Must include `boardId`.
     */
    function addItemCategory(itemCategory) {
        const { items, organization, ...category } = itemCategory;

        const itemOrderedIds = [];
        items?.forEach((item) => {
            itemsById.value[item.id] = {
                ...item,
                itemCategoryId: category.id,
                boardId: category.boardId,
                boardCategoryId: category.boardCategoryId,
                workspaceId: category.workspaceId,
                workspaceCategoryId: category.workspaceCategoryId,
            };
            itemOrderedIds.push(item.id);
        });

        itemCategoriesById.value[category.id] = {
            ...category,
            itemOrderedIds,
        };

        const parent = getBoardById(category.boardId ?? category.board_id);
        if (parent) {
            const ids = [...(parent.itemCategoryOrderedIds || [])];
            ids.splice(category.order - 1, 0, category.id);
            parent.itemCategoryOrderedIds = ids;
        }
    }

    /**
     * @param {Object} item - Must include `itemCategoryId`.
     */
    function addItem(itemData) {
        itemsById.value[itemData.id] = { ...itemData };
        const parent = getItemCategoryById(
            itemData.itemCategoryId ?? itemData.item_category_id,
        );
        if (parent) {
            parent.itemOrderedIds.push(itemData.id);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // LOCAL — SYNC
    // Merges a partial patch object into an existing entity via Object.assign.
    // No-ops silently when the entity is not found — safe to call from live-sync
    // handlers where the entity may not yet be loaded locally.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncWorkspaceCategoryById(id, patch) {
        const entity = getWorkspaceCategoryById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncWorkspaceById(id, patch) {
        const entity = getWorkspaceById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncBoardCategoryById(id, patch) {
        const entity = getBoardCategoryById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncBoardById(id, patch) {
        const entity = getBoardById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncItemCategoryById(id, patch) {
        const entity = getItemCategoryById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    /**
     * @param {number} id
     * @param {Object} patch
     */
    function syncItemById(id, patch) {
        const entity = getItemById(id);
        if (!entity) return;
        Object.assign(entity, patch);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // LOCAL — REMOVE
    // Each remove function cascades down to all descendants before removing the
    // entity itself, ensuring no orphaned entries are left in any lookup table.
    // Also removes the entity ID from the parent's ordered ID array to keep the
    // tree computeds consistent. No-ops when the entity is not found.
    // ─────────────────────────────────────────────────────────────────────────

    /** @param {number} id */
    function removeWorkspaceCategoryById(id) {
        const category = getWorkspaceCategoryById(id);
        if (category) {
            category.workspaceOrderedIds?.forEach((wsId) => {
                removeWorkspaceById(wsId);
            });
            workspaceCategoryOrderedIds.value =
                workspaceCategoryOrderedIds.value.filter(
                    (catId) => catId !== id,
                );
        }
        delete workspaceCategoriesById.value[id];
    }

    /** @param {number} id */
    function removeWorkspaceById(id) {
        const workspace = getWorkspaceById(id);
        if (workspace) {
            workspace.boardCategoryOrderedIds?.forEach((catId) => {
                removeBoardCategoryById(catId);
            });
            const parent = getWorkspaceCategoryById(
                workspace.workspaceCategoryId,
            );
            if (parent) {
                parent.workspaceOrderedIds = parent.workspaceOrderedIds.filter(
                    (wsId) => wsId !== id,
                );
            }
        }
        delete workspacesById.value[id];
    }

    /** @param {number} id */
    function removeBoardCategoryById(id) {
        const boardCategory = getBoardCategoryById(id);
        if (boardCategory) {
            boardCategory.boardOrderedIds?.forEach((boardId) => {
                removeBoardById(boardId);
            });
            const parent = getWorkspaceById(boardCategory.workspaceId);
            if (parent) {
                parent.boardCategoryOrderedIds =
                    parent.boardCategoryOrderedIds.filter(
                        (catId) => catId !== id,
                    );
            }
        }
        delete boardCategoriesById.value[id];
    }

    /** @param {number} id */
    function removeBoardById(id) {
        const board = getBoardById(id);
        if (board) {
            board.itemCategoryOrderedIds?.forEach((catId) => {
                removeItemCategoryById(catId);
            });
            const parent = getBoardCategoryById(board.boardCategoryId);
            if (parent) {
                parent.boardOrderedIds = parent.boardOrderedIds.filter(
                    (bId) => bId !== id,
                );
            }
        }
        delete boardsById.value[id];
    }

    /** @param {number} id */
    function removeItemCategoryById(id) {
        const itemCategory = getItemCategoryById(id);
        if (itemCategory) {
            itemCategory.itemOrderedIds?.forEach((itemId) => {
                removeItemById(itemId);
            });
            const parent = getBoardById(itemCategory.boardId);
            if (parent) {
                parent.itemCategoryOrderedIds =
                    parent.itemCategoryOrderedIds.filter(
                        (catId) => catId !== id,
                    );
            }
        }
        delete itemCategoriesById.value[id];
    }

    /** @param {number} id */
    function removeItemById(id) {
        const item = getItemById(id);
        if (item) {
            const parent = getItemCategoryById(item.itemCategoryId);
            if (parent) {
                parent.itemOrderedIds = parent.itemOrderedIds.filter(
                    (iId) => iId !== id,
                );
            }
        }
        delete itemsById.value[id];
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — LOAD
    // Fetch and hydrate the full layer for the given context. Call these when
    // navigating into a workspace or board for the first time. They replace the
    // entire layer's data in the lookup tables — not additive.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Loads all workspace categories and their workspaces.
     *
     * @param {boolean} [noLoading=false] - When true, do not toggle loading refs.
     */
    function loadWorkspaceCategories(noLoading = false) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get(spaceCategoriesUrl.value);
                placeWorkspaceCategoriesData(data);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isLoadingSpaces.value = val),
            noLoading,
        );
    }

    /**
     * Loads all board categories and boards for the given workspace.
     *
     * @param {Object} workspace - Must include `slug`, `id`, and `workspaceCategoryId`.
     * @param {boolean} [force=false] - When true, refetches even if boards are already present (e.g. after import).
     * @param {boolean} [noLoading=false] - When true, do not toggle loading refs.
     */
    function loadBoards(workspace, force = false, noLoading = false) {
        const space = getWorkspaceById(workspace.id);
        if (!space || space.is_being_created) {
            return;
        }
        if (!force && space?.boardCategoryOrderedIds?.length) {
            // ignore if there is only one board category and it has no boards
            const firstBoardcategory = getBoardCategoryById(
                space?.boardCategoryOrderedIds[0],
            );
            if (
                space?.boardCategoryOrderedIds?.length > 1 ||
                firstBoardcategory?.boardOrderedIds?.length
            ) {
                return;
            }
        }

        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get(
                    `/workspaces/${workspace.slug}/boards`,
                );
                placeBoardCategoriesData(data, workspace);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isLoadingBoards.value = val),
            noLoading,
        );
    }

    /**
     * Loads all item categories and items for the given board.
     *
     * @param {Object} workspace - Must include `slug`.
     * @param {Object} board     - Must include `slug` and full ancestry IDs.
     * @param {boolean} [force=false] - When true, refetches even if items are already present (e.g. after import).
     * @param {boolean} [noLoading=false] - When true, do not toggle loading refs.
     */
    function loadItems(workspace, board, force = false, noLoading = false) {
        if (!workspace || !board) return;
        if (!force && getBoardById(board.id)?.itemCategoryOrderedIds?.length) {
            return;
        }

        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get(
                    `/workspaces/${workspace.slug}/boards/${board.slug}/items`,
                );
                placeItemCategoriesData(data, board);
                const updatedBoard = getBoardById(board.id);
                newItemCategoryId.value =
                    updatedBoard?.itemCategoryOrderedIds[0];
            },
            notifyWorkspaceTreeApiError,
            (val) => (isLoadingItems.value = val),
            noLoading,
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // AUTO-LOAD WATCHERS
    // Boards are loaded automatically whenever the active workspace changes.
    // Items are loaded automatically whenever the active board changes.
    // Components should never need to call loadBoards / loadItems directly.
    // ─────────────────────────────────────────────────────────────────────────

    watch(activeWorkspaceId, (id) => {
        if (id) {
            loadBoards(workspacesById.value[id]);
        }
    });

    watch(activeBoardId, (id) => {
        if (id) {
            loadItems(activeWorkspace.value, getBoardById(id));
        }
    });

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — CREATE
    // POST to the server and immediately insert the returned entity into the
    // local state via the corresponding `add*` function. The ancestry IDs that
    // the server does not echo back are re-attached from context before adding.
    // Always returns the raw server response for callers that need it.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {Object} payload
     * @returns {Promise<Object>}
     */
    function createWorkspaceCategory(payload, fakeId = null) {
        return manageAsyncOperation(
            async () => {
                clearPendingNewWorkspace();
                const { data } = await axios.post(
                    spaceCategoriesUrl.value,
                    payload,
                );
                if (fakeId) removeWorkspaceCategoryById(fakeId);
                addWorkspaceCategory(data);
                return getWorkspaceCategoryById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                if (fakeId) removeWorkspaceCategoryById(fakeId);
            },
            (val) => (isDefiningWorkspaceCategory.value = val),
        );
    }

    /**
     * @param {Object} payload
     * @param {String} workspaceCategoryId - Parent category.
     * @returns {Promise<Object>}
     */
    function createWorkspace(payload, workspaceCategoryId) {
        const fakeId = `fake-${Date.now()}`;
        addWorkspace({
            id: fakeId,
            name: payload.name,
            is_being_created: true,
            workspaceCategoryId,
            boardCategoryOrderedIds: [],
        });

        return manageAsyncOperation(
            async () => {
                clearPendingNewWorkspace();
                const { data } = await axios.post(spacesUrl.value, payload);
                removeWorkspaceById(fakeId);
                addWorkspace({
                    ...data,
                    board_categories: [],
                    workspaceCategoryId,
                });
                return getWorkspaceById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                removeWorkspaceById(fakeId);
            },
            (val) => (isDefiningWorkspace.value = val),
        );
    }

    /**
     * @param {Object} payload
     * @returns {Promise<Object>}
     */
    function createBoardCategory(payload, fakeId = null) {
        const workspaceId = payload.workspace_id || activeWorkspaceId.value;
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(
                    boardCategoriesUrl.value,
                    payload,
                );
                if (fakeId) removeBoardCategoryById(fakeId);
                addBoardCategory({
                    ...data,
                    workspaceId,
                });
                return getBoardCategoryById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                if (fakeId) removeBoardCategoryById(fakeId);
            },
            (val) => (isDefiningBoardCategory.value = val),
        );
    }

    /**
     * @param {Object} payload
     * @param {Object} boardCategory - Parent category.
     * @returns {Promise<Object>}
     */
    function createBoard(payload, boardCategory) {
        const fakeId = `fake-${Date.now()}`;
        const parent = getBoardCategoryById(boardCategory.id);

        addBoard({
            id: fakeId,
            name: payload.name,
            is_being_created: true,
            boardCategoryId: boardCategory.id,
            workspaceId: parent?.workspaceId || boardCategory.workspaceId,
            item_categories: [],
        });

        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(boardsUrl.value, payload);
                removeBoardById(fakeId);
                addBoard({
                    ...data,
                    boardCategoryId: boardCategory.id,
                });
                return getBoardById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                removeBoardById(fakeId);
            },
            (val) => (isDefiningBoard.value = val),
        );
    }

    /**
     * @param {Object} payload
     * @returns {Promise<Object>}
     */
    function createItemCategory(payload, fakeId = null) {
        const boardId = payload.board_id || activeBoardId.value;
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(
                    itemCategoriesUrl.value,
                    payload,
                );
                if (fakeId) removeItemCategoryById(fakeId);
                addItemCategory({
                    ...data,
                    boardId,
                });
                newItemCategoryId.value = data.id;
                return getItemCategoryById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                if (fakeId) removeItemCategoryById(fakeId);
            },
            (val) => (isDefiningItemCategory.value = val),
        );
    }

    /**
     * @param {Object} payload
     * @param {Object} itemCategory - Parent category.
     * @returns {Promise<Object>}
     */
    function createItem(payload, itemCategory) {
        const fakeId = payload.fakeId || `fake-${Date.now()}`;
        const parent = getItemCategoryById(itemCategory.id);

        addItem({
            id: fakeId,
            name: payload.name,
            is_being_created: true,
            itemCategoryId: itemCategory.id,
            boardId: parent?.boardId || itemCategory.boardId,
        });

        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(itemsUrl.value, payload);
                removeItemById(fakeId);
                addItem({
                    ...data,
                    itemCategoryId: itemCategory.id,
                    boardId: parent?.boardId || itemCategory.boardId,
                });
                return getItemById(data.id);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                removeItemById(fakeId);
            },
            (val) => (isDefiningItem.value = val),
        );
    }

    /**
     * Inserts a temporary placeholder item category into the flat store so the
     * UI can render an inline creation row before the API call is made.
     * Call `cancelNewItemCategory` or `createItemCategory` (with the returned
     * fake ID) to clean up the placeholder when done.
     *
     * @param {number|string} boardId - ID of the parent board.
     * @returns {string} The generated fake ID (e.g. `"fake-category-1712345678901"`).
     */
    function initiateNewItemCategory(boardId) {
        const fakeId = `fake-category-${Date.now()}`;
        addItemCategory({
            id: fakeId,
            name: "",
            is_being_created: true,
            boardId,
            itemOrderedIds: [],
            order: 1,
        });
        return fakeId;
    }

    /**
     * Removes a temporary item category placeholder from the flat store.
     * Call this when the user cancels the inline creation row.
     *
     * @param {string} fakeId - The fake ID returned by `initiateNewItemCategory`.
     */
    function cancelNewItemCategory(fakeId) {
        removeItemCategoryById(fakeId);
    }

    /**
     * Inserts a temporary placeholder board category into the flat store so the
     * UI can render an inline creation row before the API call is made.
     * Call `cancelNewBoardCategory` or `createBoardCategory` (with the returned
     * fake ID) to clean up the placeholder when done.
     *
     * @param {number|string} workspaceId - ID of the parent workspace.
     * @returns {string} The generated fake ID (e.g. `"fake-category-1712345678901"`).
     */
    function initiateNewBoardCategory(workspaceId) {
        const fakeId = `fake-category-${Date.now()}`;
        addBoardCategory({
            id: fakeId,
            name: "",
            is_being_created: true,
            workspaceId,
            boardOrderedIds: [],
            order: 1,
        });
        return fakeId;
    }

    /**
     * Removes a temporary board category placeholder from the flat store.
     * Call this when the user cancels the inline creation row.
     *
     * @param {string} fakeId - The fake ID returned by `initiateNewBoardCategory`.
     */
    function cancelNewBoardCategory(fakeId) {
        removeBoardCategoryById(fakeId);
    }

    /**
     * Inserts a temporary placeholder workspace category into the flat store so
     * the UI can render an inline creation row before the API call is made.
     * Call `cancelNewWorkspaceCategory` or `createWorkspaceCategory` (with the
     * returned fake ID) to clean up the placeholder when done.
     *
     * @returns {string} The generated fake ID (e.g. `"fake-category-1712345678901"`).
     */
    function initiateNewWorkspaceCategory() {
        const fakeId = `fake-category-${Date.now()}`;
        addWorkspaceCategory({
            id: fakeId,
            name: "",
            is_being_created: true,
            workspaceOrderedIds: [],
            order: 1,
        });
        return fakeId;
    }

    /**
     * Removes a temporary workspace category placeholder from the flat store.
     * Call this when the user cancels the inline creation row.
     *
     * @param {string} fakeId - The fake ID returned by `initiateNewWorkspaceCategory`.
     */
    function cancelNewWorkspaceCategory(fakeId) {
        removeWorkspaceCategoryById(fakeId);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — UPDATE
    // Optimistic: the name is synced locally BEFORE the API call so the UI
    // reflects the change immediately. The slug (which the server regenerates
    // from the name) is synced AFTER the response comes back.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {Object} workspaceCategory - Must include `id`, `slug`, and `name`.
     * @returns {Promise<Object>}
     */
    function updateWorkspaceCategory(workspaceCategory) {
        syncWorkspaceCategoryById(workspaceCategory.id, {
            name: workspaceCategory.name,
        });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${spaceCategoriesUrl.value}/${workspaceCategory.slug}`,
                    { name: workspaceCategory.name },
                );
                syncWorkspaceCategoryById(workspaceCategory.id, {
                    slug: data.slug,
                });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningWorkspaceCategory.value = val),
        );
    }

    /**
     * @param {Object} workspace - Must include `id`, `slug`, and `name`.
     * @returns {Promise<Object>}
     */
    function updateWorkspace(workspace) {
        syncWorkspaceById(workspace.id, { name: workspace.name });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${spacesUrl.value}/${workspace.slug}`,
                    { name: workspace.name },
                );
                syncWorkspaceById(workspace.id, { slug: data.slug });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningWorkspace.value = val),
        );
    }

    /**
     * @param {Object} boardCategory - Must include `id`, `slug`, and `name`.
     * @returns {Promise<Object>}
     */
    function updateBoardCategory(boardCategory) {
        syncBoardCategoryById(boardCategory.id, { name: boardCategory.name });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${boardCategoriesUrl.value}/${boardCategory.slug}`,
                    { name: boardCategory.name },
                );
                syncBoardCategoryById(boardCategory.id, { slug: data.slug });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningBoardCategory.value = val),
        );
    }

    /**
     * @param {Object} board - Must include `id`, `slug`, and `name`.
     * @returns {Promise<Object>}
     */
    function updateBoard(board) {
        syncBoardById(board.id, { name: board.name });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${boardsUrl.value}/${board.slug}`,
                    { name: board.name },
                );
                syncBoardById(board.id, { slug: data.slug });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningBoard.value = val),
        );
    }

    /**
     * @param {Object} itemCategory - Must include `id`, `slug`, and `name`.
     * @returns {Promise<Object>}
     */
    function updateItemCategory(itemCategory) {
        syncItemCategoryById(itemCategory.id, { name: itemCategory.name });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${itemCategoriesUrl.value}/${itemCategory.slug}`,
                    { name: itemCategory.name },
                );
                syncItemCategoryById(itemCategory.id, { slug: data.slug });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningItemCategory.value = val),
        );
    }

    /**
     * @param {Object} itemCategory - Item category in the flat store.
     * @param {boolean} value - `automatic_close`; when enabling, also sets `automatic_end_assignment`.
     * @returns {Promise<Object|null>}
     */
    function updateItemCategoryAutomaticClose(itemCategory, value) {
        const enabled = Boolean(value);
        const patch = {
            automatic_close: enabled,
            automatic_end_assignment: enabled,
        };
        const storeEntity = getItemCategoryEntityForApi(itemCategory);
        const canonicalId = storeEntity?.id ?? itemCategory.id;
        syncItemCategoryById(canonicalId, patch);

        return manageAsyncOperation(
            async () => {
                const entity =
                    storeEntity ?? getItemCategoryEntityForApi(itemCategory);
                const slug = entity?.slug ?? itemCategory.slug;
                if (!slug) {
                    return null;
                }
                let base = itemCategoriesUrl.value;
                if (!base && entity) {
                    base = getItemCategoriesUrlForWorkspaceAndBoard(
                        getWorkspaceById(entity.workspaceId),
                        getBoardById(entity.boardId),
                    );
                }
                if (!base) {
                    return null;
                }
                const payload = entity
                    ? { ...entity }
                    : { ...itemCategory, ...patch };
                const { data } = await axios.patch(`${base}/${slug}`, payload);
                if (data?.slug != null) {
                    syncItemCategoryById(canonicalId, { slug: data.slug });
                }
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningItemCategory.value = val),
        );
    }

    /**
     * @param {Object} item - Must include `id` and `slug`. Full item object is sent as the body.
     * @returns {Promise<Object>}
     */
    function updateItem(item) {
        syncItemById(item.id, { name: item.name });
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `${itemsUrl.value}/${item.slug}`,
                    item,
                );
                syncItemById(item.id, { slug: data.slug });
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningItem.value = val),
        );
    }

    /**
     * Overwrites the top-level workspace category order array.
     * Used by the drag-and-drop layer to reflect a reorder before the API call is made.
     *
     * @param {Array<number>} orderedIds - The new ordered array of workspace category IDs.
     */
    function reOrderworkspaceCategoryOrderedIds(orderedIds) {
        workspaceCategoryOrderedIds.value = orderedIds;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — REORDER
    // Sends the new `order` and target `category_id` to the server, then
    // optimistically syncs the `order` field locally. Full re-ordering of the
    // `*OrderedIds` array on the parent is handled by the drag-and-drop layer
    // in the component before this call is made.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * @param {Object} workspaceCategory
     * @param {Object} payload - { order, before_id }
     */
    function reorderWorkspaceCategory(workspaceCategory, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(
                    `${spaceCategoriesUrl.value}/${workspaceCategory.slug}`,
                    payload,
                );
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingWorkspace.value = val),
        );
    }

    /**
     * Moves a workspace to a new position, optionally within a different category.
     *
     * @param {Object} workspace
     * @param {Object} payload - { order, category_id, before_id }
     */
    function reorderWorkspace(workspace, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(
                    `${spacesUrl.value}/${workspace.slug}`,
                    payload,
                );
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingWorkspace.value = val),
        );
    }

    /**
     * @param {Object} boardCategory
     * @param {Object} payload - { order, before_id }
     */
    function reorderBoardCategory(boardCategory, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(
                    `${boardCategoriesUrl.value}/${boardCategory.slug}`,
                    payload,
                );
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingBoard.value = val),
        );
    }

    /**
     * Moves a board to a new position, optionally within a different board category.
     *
     * @param {Object} board
     * @param {Object} payload - { order, category_id, before_id }
     */
    function reorderBoard(board, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(`${boardsUrl.value}/${board.slug}`, payload);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingBoard.value = val),
        );
    }

    /**
     * @param {Object} itemCategory
     * @param {Object} payload - { order, before_id }
     */
    function reorderItemCategory(itemCategory, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(
                    `${itemCategoriesUrl.value}/${itemCategory.slug}`,
                    payload,
                );
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingBoard.value = val),
        );
    }

    /**
     * Moves an item to a new position, optionally within a different item category.
     *
     * @param {Object} item
     * @param {Object} payload - { order, category_id, before_id }
     */
    function reorderItem(item, payload) {
        return manageAsyncOperation(
            async () => {
                await axios.patch(`${itemsUrl.value}/${item.slug}`, payload);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isReorderingItem.value = val),
        );
    }

    /**
     * Captures a rollback snapshot of every item category and item involved in a
     * pending move operation. The snapshot records the `itemOrderedIds` array of
     * every affected category (source and destination) and the full ancestry of
     * every moved item so they can be restored if the API call fails.
     *
     * @param {Object}   itemCategory  - Destination item category.
     * @param {number[]} movedItemIds  - IDs of the items being moved.
     * @returns {{ categorySnapshots: Map<number, number[]>, itemSnapshots: Array<Object> }}
     */
    function snapshotItemsMoveForRollback(itemCategory, movedItemIds) {
        const categorySnapshots = new Map();
        const itemSnapshots = [];
        const categoryIds = new Set([itemCategory.id]);
        movedItemIds.forEach((itemId) => {
            const item = itemsById.value[itemId];
            if (item) {
                categoryIds.add(item.itemCategoryId);
            }
        });
        categoryIds.forEach((cid) => {
            const cat = itemCategoriesById.value[cid];
            if (cat) {
                categorySnapshots.set(cid, [...cat.itemOrderedIds]);
            }
        });
        movedItemIds.forEach((itemId) => {
            const item = itemsById.value[itemId];
            if (!item) {
                return;
            }
            itemSnapshots.push({
                id: itemId,
                itemCategoryId: item.itemCategoryId,
                item_category_id: item.item_category_id,
                boardId: item.boardId,
                boardCategoryId: item.boardCategoryId,
                workspaceId: item.workspaceId,
                workspaceCategoryId: item.workspaceCategoryId,
            });
        });
        return { categorySnapshots, itemSnapshots };
    }

    /**
     * Restores the flat store to the state captured by `snapshotItemsMoveForRollback`.
     * Called in the error handler of `moveItems` when the API request fails.
     *
     * @param {{ categorySnapshots: Map<number, number[]>, itemSnapshots: Array<Object> }} snapshot
     */
    function restoreItemsMoveSnapshot(snapshot) {
        snapshot.itemSnapshots.forEach((s) => {
            syncItemById(s.id, {
                itemCategoryId: s.itemCategoryId,
                boardId: s.boardId,
                boardCategoryId: s.boardCategoryId,
                workspaceId: s.workspaceId,
                workspaceCategoryId: s.workspaceCategoryId,
            });
        });
        snapshot.categorySnapshots.forEach((ids, cid) => {
            syncItemCategoryById(cid, { itemOrderedIds: [...ids] });
        });
    }

    /**
     * Applies a items move optimistically to the local flat store without making
     * an API call. Used by both the optimistic path of `moveItems` and live-sync
     * handlers so both code paths share the same state mutation logic.
     *
     * @param {Object}   itemCategory      - Destination item category (must have `id` and full ancestry).
     * @param {number[]} effectiveSequence - The complete ordered ID array for the destination category after the move.
     * @param {number[]} movedItemIds      - IDs of the items being relocated.
     */
    function applyItemsMoveLocal(
        itemCategory,
        effectiveSequence,
        movedItemIds,
    ) {
        movedItemIds.forEach((itemId) => {
            const item = getItemById(itemId);
            if (!item) {
                return;
            }
            const sourceCategory = getItemCategoryById(item.itemCategoryId);
            if (sourceCategory?.itemOrderedIds) {
                syncItemCategoryById(sourceCategory.id, {
                    itemOrderedIds: sourceCategory.itemOrderedIds.filter(
                        (id) => id !== itemId,
                    ),
                });
            }
            syncItemById(itemId, {
                itemCategoryId: itemCategory.id,
                boardId: itemCategory.boardId,
                boardCategoryId: itemCategory.boardCategoryId,
                workspaceId: itemCategory.workspaceId,
                workspaceCategoryId: itemCategory.workspaceCategoryId,
            });
        });
        if (getItemCategoryById(itemCategory.id)) {
            syncItemCategoryById(itemCategory.id, {
                itemOrderedIds: [...effectiveSequence],
            });
        }
    }

    /**
     * Moves one or more items into a target item category.
     * Applies local hierarchy, then the reorder API; on success applies
     * `automatic_close` / `automatic_end_assignment` on items and conversations.
     *
     * Pass `workspace` and `board` to override the active-board URL (cross-board
     * moves from the CopyAndMove panel). Omit them for same-board moves.
     *
     * @param {Object}      itemCategory  - Destination category.
     * @param {Array}       childSequence - Ordered item ID sequence after the move.
     * @param {Array}       movedItemIds  - IDs of the items being moved.
     * @param {Object|null} workspace     - Override workspace (needs slug).
     * @param {Object|null} board         - Override board (needs slug).
     */
    function moveItems(
        itemCategory,
        childSequence,
        movedItemIds = [],
        workspace = null,
        board = null,
    ) {
        const movedSet = new Set(movedItemIds);
        const effectiveSequence =
            Array.isArray(childSequence) && childSequence.length > 0
                ? [...childSequence]
                : (() => {
                      const dest = itemCategoriesById.value[itemCategory.id];
                      const existing = dest?.itemOrderedIds ?? [];
                      return [
                          ...existing.filter((id) => !movedSet.has(id)),
                          ...movedItemIds,
                      ];
                  })();

        const itemsMoveSnapshot = snapshotItemsMoveForRollback(
            itemCategory,
            movedItemIds,
        );
        applyItemsMoveLocal(itemCategory, effectiveSequence, movedItemIds);

        return manageAsyncOperation(
            async () => {
                const url =
                    workspace?.slug && board?.slug
                        ? `/workspaces/${workspace.slug}/boards/${board.slug}/items/reorder`
                        : `${itemsUrl.value}/reorder`;
                await axios.patch(url, {
                    item_group_id: itemCategory.id,
                    child_sequence: effectiveSequence,
                });
                useConversation().applyDestinationItemCategoryMoveSideEffects(
                    movedItemIds,
                    itemCategory,
                );
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                restoreItemsMoveSnapshot(itemsMoveSnapshot);
            },
            (val) => (isReorderingItem.value = val),
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — DELETE
    // DELETE on the server, then remove the entity and all its descendants from
    // the local state via the cascading `remove*` functions. No optimistic
    // removal is done before the request completes to avoid flicker on failure.
    // ─────────────────────────────────────────────────────────────────────────

    /** @param {Object} workspaceCategory - Must include `id` and `slug`. */
    function deleteWorkspaceCategory(workspaceCategory) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(
                    `${spaceCategoriesUrl.value}/${workspaceCategory.slug}`,
                );
                removeWorkspaceCategoryById(workspaceCategory.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingWorkspaceCategory.value = val),
        );
    }

    /** @param {Object} workspace - Must include `id` and `slug`. */
    function deleteWorkspace(workspace) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(`${spacesUrl.value}/${workspace.slug}`);
                removeWorkspaceById(workspace.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingWorkspace.value = val),
        );
    }

    /** @param {Object} boardCategory - Must include `id` and `slug`. */
    function deleteBoardCategory(boardCategory) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(
                    `${boardCategoriesUrl.value}/${boardCategory.slug}`,
                );
                removeBoardCategoryById(boardCategory.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingBoardCategory.value = val),
        );
    }

    /** @param {Object} board - Must include `id` and `slug`. */
    function deleteBoard(board) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(`${boardsUrl.value}/${board.slug}`);
                removeBoardById(board.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingBoard.value = val),
        );
    }

    /** @param {Object} itemCategory - Must include `id` and `slug`. */
    function deleteItemCategory(itemCategory) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(
                    `${itemCategoriesUrl.value}/${itemCategory.slug}`,
                );
                removeItemCategoryById(itemCategory.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingItemCategory.value = val),
        );
    }

    /** @param {Object} item - Must include `id` and `slug`. */
    function deleteItem(item) {
        return manageAsyncOperation(
            async () => {
                await axios.delete(`${itemsUrl.value}/${item.slug}`);
                removeItemById(item.id);
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingItem.value = val),
        );
    }

    /**
     * Bulk-deletes multiple items in a single request.
     *
     * @param {number[]} itemIds
     */
    function deleteItems(itemIds) {
        return manageAsyncOperation(
            async () => {
                await axios.post(itemsUrl.value, {
                    _method: "delete",
                    ids: itemIds,
                });
                itemIds.forEach((id) => removeItemById(id));
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDeletingItem.value = val),
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — COPY / MOVE
    // Fire the API endpoint, then sync the flat tables exactly like the
    // create/delete operations: add* after a successful copy, remove* + patch
    // after a successful move. Components pass all required payload and ancestry
    // context; nothing is read from active-entity state here.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Copies one or more items into a destination item category.
     * Inserts each returned item into the flat store via `addItem`.
     *
     * @param {Object} payload       - { category_id, ids }
     * @param {Object} itemCategory  - Destination item category (must have `id`).
     * @param {Object} workspace     - Destination workspace (needs `id` and `slug`).
     * @param {Object} board         - Destination board (needs `id` and `slug`).
     */
    function copyItems(payload, itemCategory, workspace, board) {
        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${workspace.slug}/boards/${board.slug}`;
                const { data } = await axios.patch(
                    `${url}/items/copy`,
                    payload,
                );
                if (data?.flag) {
                    [...data.data].reverse().forEach((item) => {
                        addItem({
                            ...item,
                            itemCategoryId: itemCategory.id,
                            boardId: board.id,
                            boardCategoryId: getBoardById(board.id)
                                ?.boardCategoryId,
                            workspaceId: workspace.id,
                            workspaceCategoryId: getWorkspaceById(workspace.id)
                                ?.workspaceCategoryId,
                        });
                    });
                }
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningItem.value = val),
        );
    }

    /**
     * Copies an item category into a destination board.
     * Inserts the returned item category into the flat store via `addItemCategory`.
     *
     * @param {Object} itemCategory - Source item category (must have `id`).
     * @param {Object} payload      - { board_id, id }
     * @param {Object} board        - Destination board (needs `id` and `slug`).
     * @param {Object} workspace    - Destination workspace (needs `id` and `slug`).
     */
    function copyItemCategory(itemCategory, payload, board, workspace) {
        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${workspace.slug}/boards/${board.slug}`;
                const { data } = await axios.patch(
                    `${url}/item-categories/${itemCategory.id}/copy`,
                    payload,
                );
                if (data?.flag && data?.data) {
                    addItemCategory({
                        ...data.data,
                        boardId: board.id,
                        boardCategoryId: getBoardById(board.id)
                            ?.boardCategoryId,
                        workspaceId: workspace.id,
                        workspaceCategoryId: getWorkspaceById(workspace.id)
                            ?.workspaceCategoryId,
                    });
                }
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningItemCategory.value = val),
        );
    }

    /**
     * Captures a rollback snapshot of the flat-store state before an item
     * category is moved to a different board. Records the full ancestry of the
     * category and the `itemCategoryOrderedIds` arrays of both the source and
     * destination boards so they can be restored on API failure.
     *
     * @param {Object} itemCategory - Source item category (must have `id` and `boardId`).
     * @param {Object} destBoard    - Destination board (must have `id`).
     * @returns {{ categoryId: number, categorySnapshot: Object|null, sourceBoardId: number|null, sourceBoardOrdered: number[]|null, targetBoardId: number|null, targetBoardOrdered: number[]|null }}
     */
    function snapshotItemCategoryMove(itemCategory, destBoard) {
        const category = getItemCategoryById(itemCategory.id);
        const sourceBoard = getBoardById(itemCategory.boardId);
        const targetBoard = getBoardById(destBoard.id);
        return {
            categoryId: itemCategory.id,
            categorySnapshot: category
                ? {
                      boardId: category.boardId,
                      boardCategoryId: category.boardCategoryId,
                      workspaceId: category.workspaceId,
                      workspaceCategoryId: category.workspaceCategoryId,
                  }
                : null,
            sourceBoardId: sourceBoard?.id ?? null,
            sourceBoardOrdered: sourceBoard
                ? [...sourceBoard.itemCategoryOrderedIds]
                : null,
            targetBoardId: targetBoard?.id ?? null,
            targetBoardOrdered: targetBoard
                ? [...targetBoard.itemCategoryOrderedIds]
                : null,
        };
    }

    /**
     * Restores the flat store to the state captured by `snapshotItemCategoryMove`.
     * Called in the error handler of `moveItemCategory` when the API request fails.
     *
     * @param {{ categoryId: number, categorySnapshot: Object|null, sourceBoardId: number|null, sourceBoardOrdered: number[]|null, targetBoardId: number|null, targetBoardOrdered: number[]|null }} snap
     */
    function restoreItemCategoryMoveSnapshot(snap) {
        if (snap.sourceBoardId && snap.sourceBoardOrdered) {
            syncBoardById(snap.sourceBoardId, {
                itemCategoryOrderedIds: [...snap.sourceBoardOrdered],
            });
        }
        if (snap.targetBoardId && snap.targetBoardOrdered) {
            syncBoardById(snap.targetBoardId, {
                itemCategoryOrderedIds: [...snap.targetBoardOrdered],
            });
        }
        if (snap.categoryId && snap.categorySnapshot) {
            syncItemCategoryById(snap.categoryId, snap.categorySnapshot);
        }
    }

    /**
     * Applies an item category move optimistically to the local flat store without
     * making an API call. Removes the category from the source board's ordered list,
     * updates the category's ancestry to the destination board/workspace, and prepends
     * the category ID to the destination board's ordered list.
     * Used by both the optimistic path of `moveItemCategory` and live-sync handlers.
     *
     * @param {Object} itemCategory - Source item category (must have `id` and `boardId`).
     * @param {Object} destBoard    - Destination board (must have `id`).
     * @param {Object} destSpace    - Destination workspace (must have `id`).
     */
    function applyItemCategoryMoveLocal(itemCategory, destBoard, destSpace) {
        const sourceBoard = getBoardById(itemCategory.boardId);
        if (sourceBoard?.itemCategoryOrderedIds) {
            syncBoardById(sourceBoard.id, {
                itemCategoryOrderedIds:
                    sourceBoard.itemCategoryOrderedIds.filter(
                        (id) => id !== itemCategory.id,
                    ),
            });
        }
        const targetBoard = getBoardById(destBoard.id);
        syncItemCategoryById(itemCategory.id, {
            boardId: destBoard.id,
            boardCategoryId: targetBoard?.boardCategoryId,
            workspaceId: destSpace.id,
            workspaceCategoryId: getWorkspaceById(destSpace.id)
                ?.workspaceCategoryId,
        });
        if (targetBoard) {
            syncBoardById(targetBoard.id, {
                itemCategoryOrderedIds: [
                    itemCategory.id,
                    ...(targetBoard.itemCategoryOrderedIds ?? []),
                ],
            });
        }
    }

    function moveItemCategory(itemCategory, payload, destBoard, destSpace) {
        const icSnapshot = snapshotItemCategoryMove(itemCategory, destBoard);
        applyItemCategoryMoveLocal(itemCategory, destBoard, destSpace);

        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${destSpace.slug}/boards/${destBoard.slug}`;
                await axios.patch(
                    `${url}/item-categories/${itemCategory.id}/move`,
                    payload,
                );
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                restoreItemCategoryMoveSnapshot(icSnapshot);
            },
            (val) => (isDefiningItemCategory.value = val),
        );
    }

    /**
     * Copies a board into a destination board category.
     * Inserts the returned board into the flat store via `addBoard`.
     *
     * @param {Object} board             - Source board (must have `id`).
     * @param {Object} payload           - { order, board_id, category_id }
     * @param {Object} destBoardCategory - Destination board category (must have `id`).
     * @param {Object} destSpace         - Destination workspace (needs `id`, `slug`, and `workspaceCategoryId`).
     */
    function copyBoard(board, payload, destBoardCategory, destSpace) {
        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${destSpace.slug}`;
                const { data } = await axios.patch(
                    `${url}/boards/${board.id}/copy`,
                    payload,
                );
                if (data) {
                    addBoard({
                        ...data,
                        boardCategoryId: destBoardCategory.id,
                        workspaceId: destSpace.id,
                        workspaceCategoryId: getWorkspaceById(destSpace.id)
                            ?.workspaceCategoryId,
                    });
                }
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningBoard.value = val),
        );
    }

    /**
     * Captures a rollback snapshot of the flat-store state before a board is
     * moved to a different board category. Records the full ancestry of the board
     * and the `boardOrderedIds` arrays of both the source and destination categories
     * so they can be restored on API failure.
     *
     * @param {Object} board             - Board being moved (must have `id` and `boardCategoryId`).
     * @param {Object} destBoardCategory - Destination board category (must have `id`).
     * @param {*}      _                 - Unused (reserved for future use).
     * @returns {{ boardId: number, boardSnapshot: Object|null, sourceCategoryId: number|null, sourceCategoryOrdered: number[]|null, targetCategoryId: number|null, targetCategoryOrdered: number[]|null }}
     */
    function snapshotBoardMove(board, destBoardCategory, _) {
        const boardInStore = getBoardById(board.id);
        const sourceCategory = getBoardCategoryById(board.boardCategoryId);
        const targetCategory = getBoardCategoryById(destBoardCategory.id);
        return {
            boardId: board.id,
            boardSnapshot: boardInStore
                ? {
                      boardCategoryId: boardInStore.boardCategoryId,
                      workspaceId: boardInStore.workspaceId,
                      workspaceCategoryId: boardInStore.workspaceCategoryId,
                  }
                : null,
            sourceCategoryId: sourceCategory?.id ?? null,
            sourceCategoryOrdered: sourceCategory
                ? [...sourceCategory.boardOrderedIds]
                : null,
            targetCategoryId: targetCategory?.id ?? null,
            targetCategoryOrdered: targetCategory
                ? [...targetCategory.boardOrderedIds]
                : null,
        };
    }

    /**
     * Restores the flat store to the state captured by `snapshotBoardMove`.
     * Called in the error handler of `moveBoard` when the API request fails.
     *
     * @param {{ boardId: number, boardSnapshot: Object|null, sourceCategoryId: number|null, sourceCategoryOrdered: number[]|null, targetCategoryId: number|null, targetCategoryOrdered: number[]|null }} snap
     */
    function restoreBoardMoveSnapshot(snap) {
        if (snap.sourceCategoryId && snap.sourceCategoryOrdered) {
            syncBoardCategoryById(snap.sourceCategoryId, {
                boardOrderedIds: [...snap.sourceCategoryOrdered],
            });
        }
        if (snap.targetCategoryId && snap.targetCategoryOrdered) {
            syncBoardCategoryById(snap.targetCategoryId, {
                boardOrderedIds: [...snap.targetCategoryOrdered],
            });
        }
        if (snap.boardId && snap.boardSnapshot) {
            syncBoardById(snap.boardId, snap.boardSnapshot);
        }
    }

    /**
     * Applies a board move optimistically to the local flat store without making
     * an API call. Removes the board from the source category's ordered list,
     * updates the board's ancestry to the destination category/workspace, and
     * prepends the board ID to the destination category's ordered list.
     * Used by both the optimistic path of `moveBoard` and live-sync handlers.
     *
     * @param {Object} board             - Board being moved (must have `id` and `boardCategoryId`).
     * @param {Object} destBoardCategory - Destination board category (must have `id`).
     * @param {Object} destSpace         - Destination workspace (must have `id`).
     */
    function applyBoardMoveLocal(board, destBoardCategory, destSpace) {
        const sourceCategory = getBoardCategoryById(board.boardCategoryId);
        if (sourceCategory?.boardOrderedIds) {
            syncBoardCategoryById(sourceCategory.id, {
                boardOrderedIds: sourceCategory.boardOrderedIds.filter(
                    (id) => id !== board.id,
                ),
            });
        }
        syncBoardById(board.id, {
            boardCategoryId: destBoardCategory.id,
            workspaceId: destSpace.id,
            workspaceCategoryId: getWorkspaceById(destSpace.id)
                ?.workspaceCategoryId,
        });
        const targetCategory = getBoardCategoryById(destBoardCategory.id);
        if (targetCategory) {
            syncBoardCategoryById(destBoardCategory.id, {
                boardOrderedIds: [
                    board.id,
                    ...(targetCategory.boardOrderedIds ?? []),
                ],
            });
        }
    }

    function moveBoard(board, payload, destBoardCategory, destSpace) {
        const boardSnapshot = snapshotBoardMove(
            board,
            destBoardCategory,
            destSpace,
        );
        applyBoardMoveLocal(board, destBoardCategory, destSpace);

        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${destSpace.slug}`;
                await axios.patch(`${url}/boards/${board.id}/move`, payload);
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                restoreBoardMoveSnapshot(boardSnapshot);
            },
            (val) => (isDefiningBoard.value = val),
        );
    }

    /**
     * Copies a board category into a destination workspace.
     * Inserts the returned board category into the flat store via `addBoardCategory`.
     *
     * @param {Object} boardCategory - Source category (must have `id`).
     * @param {Object} payload       - { workspace_id, id }
     * @param {Object} destSpace     - Destination workspace (needs `id` and `slug`).
     */
    function copyBoardCategory(boardCategory, payload, destSpace) {
        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${destSpace.slug}`;
                const { data } = await axios.patch(
                    `${url}/board-categories/${boardCategory.id}/copy`,
                    payload,
                );
                if (data?.flag && data?.data) {
                    addBoardCategory({
                        ...data.data,
                        workspaceId: destSpace.id,
                        workspaceCategoryId: getWorkspaceById(destSpace.id)
                            ?.workspaceCategoryId,
                    });
                }
                return data;
            },
            notifyWorkspaceTreeApiError,
            (val) => (isDefiningBoardCategory.value = val),
        );
    }

    /**
     * Captures a rollback snapshot of the flat-store state before a board category
     * is moved to a different workspace. Records the full ancestry of the category
     * and the `boardCategoryOrderedIds` arrays of both the source and destination
     * workspaces so they can be restored on API failure.
     *
     * @param {Object} boardCategory - Source board category (must have `id` and `workspaceId`).
     * @param {Object} destSpace     - Destination workspace (must have `id`).
     * @returns {{ categoryId: number, categorySnapshot: Object|null, sourceWsId: number|null, sourceWsOrdered: number[]|null, targetWsId: number|null, targetWsOrdered: number[]|null }}
     */
    function snapshotBoardCategoryMove(boardCategory, destSpace) {
        const catInStore = getBoardCategoryById(boardCategory.id);
        const sourceWs = getWorkspaceById(boardCategory.workspaceId);
        const targetWs = getWorkspaceById(destSpace.id);
        return {
            categoryId: boardCategory.id,
            categorySnapshot: catInStore
                ? {
                      workspaceId: catInStore.workspaceId,
                      workspaceCategoryId: catInStore.workspaceCategoryId,
                  }
                : null,
            sourceWsId: sourceWs?.id ?? null,
            sourceWsOrdered: sourceWs
                ? [...sourceWs.boardCategoryOrderedIds]
                : null,
            targetWsId: targetWs?.id ?? null,
            targetWsOrdered: targetWs
                ? [...targetWs.boardCategoryOrderedIds]
                : null,
        };
    }

    /**
     * Restores the flat store to the state captured by `snapshotBoardCategoryMove`.
     * Called in the error handler of `moveBoardCategory` when the API request fails.
     *
     * @param {{ categoryId: number, categorySnapshot: Object|null, sourceWsId: number|null, sourceWsOrdered: number[]|null, targetWsId: number|null, targetWsOrdered: number[]|null }} snap
     */
    function restoreBoardCategoryMoveSnapshot(snap) {
        if (snap.sourceWsId && snap.sourceWsOrdered) {
            syncWorkspaceById(snap.sourceWsId, {
                boardCategoryOrderedIds: [...snap.sourceWsOrdered],
            });
        }
        if (snap.targetWsId && snap.targetWsOrdered) {
            syncWorkspaceById(snap.targetWsId, {
                boardCategoryOrderedIds: [...snap.targetWsOrdered],
            });
        }
        if (snap.categoryId && snap.categorySnapshot) {
            syncBoardCategoryById(snap.categoryId, snap.categorySnapshot);
        }
    }

    /**
     * Applies a board category move optimistically to the local flat store without
     * making an API call. Removes the category from the source workspace's ordered
     * list, updates the category's ancestry to the destination workspace, and
     * prepends the category ID to the destination workspace's ordered list.
     * Used by both the optimistic path of `moveBoardCategory` and live-sync handlers.
     *
     * @param {Object} boardCategory - Source board category (must have `id` and `workspaceId`).
     * @param {Object} destSpace     - Destination workspace (must have `id`).
     */
    function applyBoardCategoryMoveLocal(boardCategory, destSpace) {
        const sourceWs = getWorkspaceById(boardCategory.workspaceId);
        if (sourceWs?.boardCategoryOrderedIds) {
            syncWorkspaceById(sourceWs.id, {
                boardCategoryOrderedIds:
                    sourceWs.boardCategoryOrderedIds.filter(
                        (id) => id !== boardCategory.id,
                    ),
            });
        }
        syncBoardCategoryById(boardCategory.id, {
            workspaceId: destSpace.id,
            workspaceCategoryId: getWorkspaceById(destSpace.id)
                ?.workspaceCategoryId,
        });
        const targetWs = getWorkspaceById(destSpace.id);
        if (targetWs) {
            syncWorkspaceById(targetWs.id, {
                boardCategoryOrderedIds: [
                    boardCategory.id,
                    ...(targetWs.boardCategoryOrderedIds ?? []),
                ],
            });
        }
    }

    function moveBoardCategory(boardCategory, payload, destSpace) {
        const bcSnapshot = snapshotBoardCategoryMove(boardCategory, destSpace);
        applyBoardCategoryMoveLocal(boardCategory, destSpace);

        return manageAsyncOperation(
            async () => {
                const url = `/workspaces/${destSpace.slug}`;
                await axios.patch(
                    `${url}/board-categories/${boardCategory.id}/move`,
                    payload,
                );
            },
            (err) => {
                notifyWorkspaceTreeApiError(err);
                restoreBoardCategoryMoveSnapshot(bcSnapshot);
            },
            (val) => (isDefiningBoardCategory.value = val),
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // EXPOSE
    // ─────────────────────────────────────────────────────────────────────────

    return {
        // loading state
        isLoadingSpaces,
        isLoadingBoards,
        isLoadingItems,
        isDefiningWorkspaceCategory,
        isDefiningWorkspace,
        isDefiningBoardCategory,
        isDefiningBoard,
        isDefiningItemCategory,
        isDefiningItem,
        isDeletingWorkspaceCategory,
        isDeletingWorkspace,
        isDeletingBoardCategory,
        isDeletingBoard,
        isDeletingItemCategory,
        isDeletingItem,
        isReorderingWorkspace,
        isReorderingBoard,
        isReorderingItem,

        fetching_item,
        setIsFetchingItem,

        // active computed
        activeWorkspaceCategory,
        activeWorkspace,
        activeBoardCategory,
        activeBoard,
        activeItemCategory,
        activeItem,

        // active setters
        setActiveWorkspaceCategoryId,
        setActiveWorkspaceId,
        setActiveBoardCategoryId,
        setActiveBoardId,
        setActiveItemCategoryId,
        setActiveItemId,
        resetActiveState,

        // REST URL computeds + parameterized builders (same path rules)
        spaceCategoriesUrl,
        spacesUrl,
        boardCategoriesUrl,
        boardsUrl,
        itemCategoriesUrl,
        itemsUrl,
        getBoardCategoriesUrlForWorkspace,
        getBoardsUrlForWorkspace,
        getItemCategoriesUrlForWorkspaceAndBoard,
        getItemsUrlForWorkspaceAndBoard,

        // pending new workspace (used to show inline create row)
        pendingNewWorkspaceCategoryId,
        pendingNewWorkspaceType,
        setPendingNewWorkspace,
        clearPendingNewWorkspace,
        newItemCategoryId,

        // top-level order (writable — used by drag-and-drop)
        workspaceCategoryOrderedIds,

        // computed trees
        spaceTree,
        boardTree,
        itemTree,

        // tree builders (for any entity, not just the active one)
        getSpaceTree,
        getBoardTreeForWorkspace,
        getItemTreeForBoard,

        // getters
        getWorkspaceCategoryById,
        getWorkspaceById,
        getBoardCategoryById,
        getBoardById,
        getItemCategoryById,
        getItemById,

        // local — add
        addWorkspaceCategory,
        addWorkspace,
        addBoardCategory,
        addBoard,
        addItemCategory,
        addItem,

        // local — sync
        syncWorkspaceCategoryById,
        syncWorkspaceById,
        syncBoardCategoryById,
        syncBoardById,
        syncItemCategoryById,
        syncItemById,
        applyItemsMoveLocal,
        applyItemCategoryMoveLocal,
        applyBoardMoveLocal,
        applyBoardCategoryMoveLocal,

        // local — remove
        removeWorkspaceCategoryById,
        removeWorkspaceById,
        removeBoardCategoryById,
        removeBoardById,
        removeItemCategoryById,
        removeItemById,

        // local - fake
        initiateNewWorkspaceCategory,
        cancelNewWorkspaceCategory,
        initiateNewBoardCategory,
        cancelNewBoardCategory,
        initiateNewItemCategory,
        cancelNewItemCategory,

        // api — load
        loadWorkspaceCategories,
        loadBoards,
        loadItems,

        // api — create
        createWorkspaceCategory,
        createWorkspace,
        createBoardCategory,
        createBoard,
        createItemCategory,
        createItem,

        // api — update
        updateWorkspaceCategory,
        updateWorkspace,
        updateBoardCategory,
        updateBoard,
        updateItemCategory,
        updateItemCategoryAutomaticClose,
        updateItem,

        // api — reorder
        reOrderworkspaceCategoryOrderedIds,
        reorderWorkspaceCategory,
        reorderWorkspace,
        reorderBoardCategory,
        reorderBoard,
        reorderItemCategory,
        reorderItem,
        moveItems,

        // api — copy / move
        copyItems,
        copyItemCategory,
        moveItemCategory,
        copyBoard,
        moveBoard,
        copyBoardCategory,
        moveBoardCategory,

        // api — delete
        deleteWorkspaceCategory,
        deleteWorkspace,
        deleteBoardCategory,
        deleteBoard,
        deleteItemCategory,
        deleteItem,
        deleteItems,
    };
});
